import { PushTestSchema, type PushTest } from '../meta/online/hub';
import { apiRequest } from '../platform/online';
import { onPushTapped, pushAllowed, pushSupported, pushToken } from '../platform/push';
import { openDailyBoardFromPush, openInboxFromPush, showInviteFromPush } from './hub';
import { ensurePlayer, onlineOn } from './online';
import { saves } from './records';

/**
 * Friend alerts, game side (docs/FRIENDS.md, step 4): push notifications when a friend invites
 * this player, asks to be friends, or is waiting in their room, while the app is closed. On by
 * default, once the phone allows notifications (asked once after the first lawn, notifyAsk.ts);
 * the switch is in Settings > ONLINE.
 *
 * `online.pushSent` is `player:token` as the server last stored it; while it is set with alerts off,
 * taking it back is retried. The token is sent again at every launch and before a test (the server
 * forgets one Apple calls dead, and a test should run on the token the phone has now).
 */

/** How turning alerts on or off went: `noToken` is Apple not giving this phone a token. */
export type PushChange = 'on' | 'off' | 'blocked' | 'offline' | { noToken: string };

/** Whether the switch shows at all: an iPhone with online play on. */
export function friendAlertsAvailable(): boolean {
  return pushSupported() && onlineOn();
}

export function friendAlertsOn(): boolean {
  return friendAlertsAvailable() && saves().save.settings.friendAlerts;
}

/** Turns friend alerts on (asking for notifications first) or off. */
export async function setFriendAlerts(on: boolean): Promise<PushChange> {
  if (!on) {
    saves().update((s) => (s.settings.friendAlerts = false));
    void takeBack();
    return 'off';
  }
  if (!(await pushAllowed(true))) return 'blocked';
  const sent = await sendToken();
  if (sent !== 'on') return sent;
  saves().update((s) => (s.settings.friendAlerts = true));
  return 'on';
}

/** Gives the server this phone's token (unless it already has it for this player, or `again`). */
async function sendToken(again = false): Promise<'on' | 'offline' | { noToken: string }> {
  const player = await ensurePlayer();
  if (!player) return 'offline';
  const got = await pushToken();
  if ('error' in got) return { noToken: got.error };
  const sent = `${player.id}:${got.token}`;
  if (!again && saves().save.online.pushSent === sent) return 'on';
  const answer = await apiRequest('/me/push', {
    method: 'PUT',
    token: player.token,
    body: { platform: 'ios', token: got.token },
  });
  if (answer.status !== 204) return 'offline';
  saves().update((s) => (s.online.pushSent = sent));
  return 'on';
}

/** Asks the server to forget this phone's token (kept trying at launches until it does). */
async function takeBack(): Promise<void> {
  const player = saves().save.online.player;
  if (!saves().save.online.pushSent) return;
  if (!player) {
    saves().update((s) => delete s.online.pushSent);
    return;
  }
  const answer = await apiRequest('/me/push', { method: 'DELETE', token: player.token });
  if (answer.status === 204 || answer.status === 401) {
    saves().update((s) => delete s.online.pushSent);
  }
}

/**
 * At launch: a tapped alert opens what it was about (the invite's card, or the INBOX), and the
 * server's copy of the token is brought up to date (the token or the player may have changed,
 * notifications may have been switched off in the phone's Settings, or online play turned off).
 */
export function initPush(): void {
  if (!pushSupported()) return;
  onPushTapped((data) => {
    const code = typeof data.code === 'string' ? data.code : null;
    if (data.kind === 'invite' && code) showInviteFromPush(code);
    else if (data.kind === 'request') openInboxFromPush();
    else if (data.kind === 'daily') openDailyBoardFromPush();
    // A friend in the room: the app opening is enough (the lobby is still up).
  });
  void (async () => {
    if (friendAlertsOn() && (await pushAllowed(false))) await sendToken(true);
    else if (!friendAlertsOn()) await takeBack();
  })();
}

/**
 * Settings > ONLINE > SEND A TEST: the token sent again, then a test alert. Apple's answer (or why
 * there is no token), or null when the server can't be reached.
 */
export async function testFriendAlert(): Promise<PushTest | null> {
  const token = await sendToken(true);
  if (token === 'offline') return null;
  if (token !== 'on') return { sent: false, status: 0, reason: token.noToken };
  const player = await ensurePlayer();
  if (!player) return null;
  const answer = await apiRequest('/me/push/test', { method: 'POST', token: player.token });
  if (answer.status !== 200) return null;
  const parsed = PushTestSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}
